/**
 * A form similar to FormColumn, but uses a DDM with 2 columns
 */
import { Controller, useFormContext } from 'react-hook-form';
import { useRef } from 'react';

import DoubleColumnSelect from './DoubleColumnSelect';
import { getSimplePrescriptionValuesArray } from '@utils/utils';
import { MenuItem, Select } from '@mui/material';
import ArrowDown from '@images/svg-icons/arrowDown.svg';

interface EyeProps {
    enabled: boolean;
    name: string;
    defaultValue?: string;
    values: string[] | number[];
}

interface DropdownProps {
    attribute: string;
    label: string;
    left?: EyeProps;
    right?: EyeProps;
}

const visuallyHiddenStyles = {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute' as const,
    width: '1px',
};

const sanitizeIdPart = value => `${value || ''}`.replace(/[^a-zA-Z0-9_-]/g, '-');

const menuOptions = {
    PaperProps: {
        style: {
            maxHeight: 220, // set a maximum height for the menu
        },
    },
};

const FormDoubleColumn = ({ attribute, label, left, right }: DropdownProps) => {
    const { register, control } = useFormContext();
    const idBaseRef = useRef(`presc-${Math.random().toString(36).slice(2, 10)}`);
    const idBase = idBaseRef.current;
    const fieldLabelId = `${idBase}-${sanitizeIdPart(attribute)}-field-label`;
    const rightEyeLabelId = `${idBase}-${sanitizeIdPart(
        right?.name || `${attribute}-right`
    )}-eye-label`;
    const leftEyeLabelId = `${idBase}-${sanitizeIdPart(
        left?.name || `${attribute}-left`
    )}-eye-label`;

    console.log('left values: ', left?.values);

    console.log('right values: ', right?.values);

    //returns true if contains both strictly negative and strictly positive values, false otherwise
    const hasBothValuesRange = values => {
        if (!values) return false;
        var hasNeg = false;
        var hasPos = false;
        for (let i = 0; i < values.length; i++) {
            if (parseFloat(values[i]) < 0) hasNeg = true;
            else if (parseFloat(values[i]) > 0) hasPos = true;
            if (hasNeg && hasPos) return true;
        }
        return false;
    };

    return (
        <>
            {(right?.values?.length || left?.values?.length) && (
                <div
                    className={`${attribute}-column param-column ${
                        right?.values.length === 1 && left?.values.length === 1
                            ? 'single-value'
                            : ''
                    }`}
                >
                    <div className={`param-label ${attribute}-label`}>
                        <label id={fieldLabelId}>{label}</label>
                    </div>

                    {!!right && (
                        <div className={`presc-param-select ${attribute}-select right`}>
                            <span id={rightEyeLabelId} style={visuallyHiddenStyles}>
                                Right eye
                            </span>
                            {right?.values.length === 1 ? (
                                <>
                                    <input
                                        type="hidden"
                                        {...register(right?.name)}
                                        value={right?.values[0]}
                                    />
                                    {right?.values[0]}
                                </>
                            ) : (
                                <Controller
                                    name={right?.name}
                                    control={control}
                                    defaultValue=""
                                    render={({ field: { onChange, value } }) =>
                                        hasBothValuesRange(right?.values) ? (
                                            <DoubleColumnSelect
                                                aria-labelledby={`${rightEyeLabelId} ${fieldLabelId}`}
                                                id={`${right?.name}-select`}
                                                name={right?.name}
                                                value={value}
                                                disabled={!right?.enabled}
                                                register={register(right?.name)}
                                                onChange={onChange}
                                                options={getSimplePrescriptionValuesArray(
                                                    right?.values,
                                                    2
                                                ).map((e, key) => {
                                                    return (
                                                        <option key={key} value={e.value}>
                                                            {e.name}
                                                        </option>
                                                    );
                                                })}
                                            />
                                        ) : (
                                            <Select
                                                SelectDisplayProps={{
                                                    'aria-labelledby': `${rightEyeLabelId} ${fieldLabelId}`,
                                                }}
                                                disabled={!right?.enabled}
                                                value={value}
                                                onChange={onChange}
                                                defaultValue={right?.defaultValue || ''}
                                                className={`param-select`}
                                                IconComponent={props => (
                                                    <ArrowDown
                                                        {...props}
                                                        title="Open dropdown"
                                                        aria-hidden="true"
                                                    />
                                                )}
                                                MenuProps={menuOptions}
                                            >
                                                {right?.values
                                                    .sort((a, b) => {
                                                        if (a === '+0.00' && b !== '+0.00')
                                                            return -1;
                                                        if (b === '+0.00' && a !== '+0.00')
                                                            return 1;

                                                        if (
                                                            typeof a === 'number' &&
                                                            typeof b === 'number'
                                                        ) {
                                                            if (a < 0 && b < 0) {
                                                                return a - b;
                                                            } else if (a > 0 && b > 0) {
                                                                return a - b;
                                                            }
                                                            return a - b;
                                                        } else if (
                                                            typeof a === 'string' &&
                                                            typeof b === 'string'
                                                        ) {
                                                            return a.localeCompare(b);
                                                        }
                                                        return 0;
                                                    })
                                                    .map((v: string | number) => (
                                                        <MenuItem key={v} value={v} tabIndex={0}>
                                                            {v}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        )
                                    }
                                />
                            )}
                        </div>
                    )}

                    {!!left && (
                        <div className={`presc-param-select ${attribute}-select left`}>
                            <span id={leftEyeLabelId} style={visuallyHiddenStyles}>
                                Left eye
                            </span>
                            {left?.values.length === 1 ? (
                                <>
                                    <input
                                        type="hidden"
                                        {...register(left?.name)}
                                        value={left?.values[0]}
                                    />
                                    {left?.values[0]}
                                </>
                            ) : (
                                <Controller
                                    name={left?.name}
                                    control={control}
                                    defaultValue=""
                                    render={({ field: { onChange, value } }) =>
                                        hasBothValuesRange(left?.values) ? (
                                            <DoubleColumnSelect
                                                aria-labelledby={`${leftEyeLabelId} ${fieldLabelId}`}
                                                id={`${left?.name}-select`}
                                                name={left?.name}
                                                value={value}
                                                disabled={!left?.enabled}
                                                register={register(left?.name)}
                                                onChange={onChange}
                                                options={getSimplePrescriptionValuesArray(
                                                    left?.values,
                                                    2
                                                ).map((e, key) => {
                                                    return (
                                                        <option key={key} value={e.value}>
                                                            {e.name}
                                                        </option>
                                                    );
                                                })}
                                            />
                                        ) : (
                                            <Select
                                                SelectDisplayProps={{
                                                    'aria-labelledby': `${leftEyeLabelId} ${fieldLabelId}`,
                                                }}
                                                disabled={!left?.enabled}
                                                value={value}
                                                onChange={onChange}
                                                defaultValue={left?.defaultValue || ''}
                                                className={`param-select`}
                                                IconComponent={props => (
                                                    <ArrowDown
                                                        {...props}
                                                        title="Open dropdown"
                                                        aria-hidden="true"
                                                    />
                                                )}
                                                MenuProps={menuOptions}
                                            >
                                                {left?.values.map((v: string | number) => (
                                                    <MenuItem key={v} value={v} tabIndex={0}>
                                                        {v}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )
                                    }
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default FormDoubleColumn;
